/**
 * CollaborationSettingsDrawer Component
 * Side panel for managing collaborators, roles, and permissions
 * Triggered by settings icon in CollaborationManager
 */

import React, { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  XMarkIcon,
  UserPlusIcon,
  CogIcon,
  TrashIcon,
  PencilIcon,
  UserGroupIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import { CollaborationUser } from '../../edgedocs/types/Collaboration.types';
import { UserAvatar } from './UserAvatar';

export interface CollaborationSettingsDrawerProps {
  /** Whether the drawer is open */
  isOpen: boolean;
  
  /** Close drawer callback */
  onClose: () => void;
  
  /** Document ID */
  documentId: string;
  
  /** Version ID - if provided, shows assignment interface for this version */
  versionId?: string;
  
  /** Version number (e.g., "v1.0") - used for display when versionId is provided */
  versionNumber?: string;
  
  /** Document title - used for display */
  documentTitle?: string;
  
  /** Current collaborators */
  collaborators: CollaborationUser[];
  
  /** Current user */
  currentUser?: CollaborationUser;
  
  /** Whether current user has owner permissions */
  isOwner: boolean;
  
  /** Callbacks */
  onInviteUser: () => void;
  onUpdateRole: (userId: string, newRole: CollaborationUser['role']) => void;
  onRemoveUser: (collaborator: CollaborationUser) => void;
  onUpdatePermissions: (userId: string, permissions: any) => void;
  
  /** Version-specific assignment callbacks (used when versionId is provided) */
  onAssignToVersion?: (userId: string) => void;
  onUnassignFromVersion?: (userId: string) => void;
}

interface InvitationHistoryItem {
  id: string;
  email: string;
  role: CollaborationUser['role'];
  invitedAt: Date;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  invitedBy: string;
  expiresAt?: Date;
}

interface ActivityLogItem {
  id: string;
  type: 'user_joined' | 'user_left' | 'role_changed' | 'comment_added' | 'change_tracked';
  user: string;
  description: string;
  timestamp: Date;
  metadata?: any;
}

// API helper functions
const getAuthHeaders = (token?: string) => ({
  'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const fetchCollaborationState = async (documentId: string, token?: string) => {
  const response = await fetch(`/api/v1/documents/${documentId}/collaboration`, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch collaboration state: ${response.statusText}`);
  }
  
  return response.json();
};

const fetchInvitations = async (documentId: string, token?: string) => {
  const response = await fetch(`/api/v1/documents/${documentId}/invitations`, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch invitations: ${response.statusText}`);
  }
  
  return response.json();
};

const removeCollaborator = async (documentId: string, collaboratorUserId: string, token?: string, reason?: string) => {
  const response = await fetch(`/api/v1/documents/${documentId}/collaborators/${collaboratorUserId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(token),
    body: reason ? JSON.stringify({ removal_reason: reason }) : undefined,
  });
  
  if (!response.ok) {
    throw new Error(`Failed to remove collaborator: ${response.statusText}`);
  }
  
  return response.json();
};

const fetchVersionAssignments = async (documentId: string, versionId: string, token?: string) => {
  const response = await fetch(`/api/v1/documents/${documentId}/versions/${versionId}/collaborators`, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch version assignments: ${response.statusText}`);
  }
  
  return response.json();
};

const assignUserToVersion = async (documentId: string, versionId: string, userId: string, token?: string) => {
  const response = await fetch(`/api/v1/documents/${documentId}/versions/${versionId}/assign/${userId}`, {
    method: 'POST',
    headers: getAuthHeaders(token),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to assign user to version: ${response.statusText}`);
  }
  
  return response.json();
};

const unassignUserFromVersion = async (documentId: string, versionId: string, userId: string, token?: string) => {
  const response = await fetch(`/api/v1/documents/${documentId}/versions/${versionId}/assign/${userId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(token),
  });
  
  if (!response.ok) {
    throw new Error(`Failed to unassign user from version: ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * CollaboratorCard Component
 * Individual collaborator with role management
 */
interface CollaboratorCardProps {
  collaborator: CollaborationUser;
  isOwner: boolean;
  isCurrentUser: boolean;
  canManage: boolean;
  onUpdateRole: (userId: string, newRole: CollaborationUser['role']) => void;
  onRemoveUser: (collaborator: CollaborationUser) => void;
  // Version assignment props (optional)
  isVersionMode?: boolean;
  isAssignedToVersion?: boolean;
  onAssignToVersion?: (userId: string) => void;
  onUnassignFromVersion?: (userId: string) => void;
}

const CollaboratorCard: React.FC<CollaboratorCardProps> = ({
  collaborator,
  isCurrentUser,
  canManage,
  onUpdateRole,
  onRemoveUser,
  isVersionMode = false,
  isAssignedToVersion = false,
  onAssignToVersion,
  onUnassignFromVersion,
}) => {
  const [showRoleMenu, setShowRoleMenu] = useState(false);

  const roleOptions: Array<{ value: CollaborationUser['role']; label: string; description: string }> = [
    { value: 'editor', label: 'Editor', description: 'Can edit and comment' },
    { value: 'reviewer', label: 'Reviewer', description: 'Can add comments' },
    { value: 'viewer', label: 'Viewer', description: 'Read-only access' },
  ];

  const formatLastActive = (date: Date | undefined): string => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMinutes < 1) return 'Active now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
      <div className="flex items-center space-x-3">
        <UserAvatar
          user={collaborator}
          size="md"
          showStatus={true}
          showRole={true}
        />
        <div>
          <div className="font-medium text-gray-900 dark:text-white">
            {collaborator.name || collaborator.email}
            {isCurrentUser && (
              <span className="ml-2 text-xs text-lex-accent">(You)</span>
            )}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {collaborator.email}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500">
            Last active: {formatLastActive(collaborator.lastActive)}
          </div>
        </div>
      </div>

      {canManage && !isCurrentUser && collaborator.role !== 'owner' && (
        <div className="flex items-center space-x-2">
          {/* Role dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowRoleMenu(!showRoleMenu)}
              className="px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              {collaborator.role}
            </button>
            
            {showRoleMenu && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-10">
                {roleOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onUpdateRole(collaborator.id, option.value);
                      setShowRoleMenu(false);
                    }}
                    className={`
                      w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-600
                      ${collaborator.role === option.value ? 'bg-lex-accent-soft text-lex-accent' : ''}
                    `}
                  >
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{option.description}</div>
                  </button>
                ))}
              </div>
          )}
        </div>

          {/* Version Assignment Controls - only show in version mode and if current user is owner */}
          {isVersionMode && canManage && !isCurrentUser && (
            <div className="flex items-center space-x-2">
              {isAssignedToVersion ? (
                <button
                  onClick={() => onUnassignFromVersion?.(collaborator.id)}
                  className="px-2 py-1 text-xs bg-green-100 text-green-700 border border-green-300 rounded-md hover:bg-red-100 hover:text-red-700 hover:border-red-300 transition-colors"
                  title="Click to unassign from this version"
                >
                  Unassign
                </button>
              ) : (
                <button
                  onClick={() => onAssignToVersion?.(collaborator.id)}
                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-lex-accent-soft hover:text-lex-accent hover:border-lex-accent transition-colors"
                  title="Click to assign to this version"
                >
                  Assign
                </button>
              )}
            </div>
          )}

          {/* Remove button - only show if user can manage collaborators and not in version mode */}
          {canManage && !isVersionMode && (
            <button
              onClick={() => onRemoveUser(collaborator)}
              className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
              title="Remove collaborator (owner only)"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * InvitationHistorySection Component
 */
const InvitationHistorySection: React.FC<{ 
  invitations: InvitationHistoryItem[];
  onCancelInvitation?: (invitationId: string, email: string) => Promise<void>;
}> = ({ invitations, onCancelInvitation }) => {
  const getStatusBadge = (status: InvitationHistoryItem['status']) => {
    const configs = {
      pending: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', label: 'Pending' },
      accepted: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', label: 'Accepted' },
      expired: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Expired' },
      revoked: { bg: 'bg-gray-100 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-300', label: 'Revoked' },
    };
    const config = configs[status];
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="space-y-3">
      {invitations.map((invitation) => (
        <div key={invitation.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="flex-1">
            <div className="font-medium text-gray-900 dark:text-white">{invitation.email}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Invited as {invitation.role} by {invitation.invitedBy}
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500">
              {invitation.invitedAt.toLocaleDateString()}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              {getStatusBadge(invitation.status)}
              {invitation.expiresAt && (
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Expires {invitation.expiresAt.toLocaleDateString()}
                </div>
              )}
            </div>
            {/* Cancel button - only show for pending invitations */}
            {invitation.status === 'pending' && onCancelInvitation && (
              <button
                onClick={() => onCancelInvitation(invitation.id, invitation.email)}
                className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                title="Cancel invitation"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * ActivityLogSection Component
 */
const ActivityLogSection: React.FC<{ activities: ActivityLogItem[] }> = ({ activities }) => {
  const getActivityIcon = (type: ActivityLogItem['type']) => {
    const iconMap = {
      user_joined: UserGroupIcon,
      user_left: UserGroupIcon,
      role_changed: CogIcon,
      comment_added: BellIcon,
      change_tracked: PencilIcon,
    };
    return iconMap[type] || BellIcon;
  };

  return (
    <div className="space-y-3">
      {activities.map((activity) => {
        const Icon = getActivityIcon(activity.type);
        return (
          <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm">
                <span className="font-medium text-gray-900 dark:text-white">{activity.user}</span>
                <span className="text-gray-600 dark:text-gray-300 ml-1">{activity.description}</span>
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {activity.timestamp.toLocaleString()}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Main CollaborationSettingsDrawer Component
 */
export const CollaborationSettingsDrawer: React.FC<CollaborationSettingsDrawerProps> = ({
  isOpen,
  onClose,
  documentId,
  versionId,
  versionNumber,
  documentTitle,
  collaborators,
  currentUser,
  isOwner,
  onInviteUser,
  onUpdateRole,
  onRemoveUser,
  onAssignToVersion,
  onUnassignFromVersion,
}) => {
  const { keycloak } = useKeycloak();
  const [activeTab, setActiveTab] = useState<'collaborators' | 'invitations' | 'activity'>('collaborators');
  const [invitationHistory, setInvitationHistory] = useState<InvitationHistoryItem[]>([]);
  const [activityLog, setActivityLog] = useState<ActivityLogItem[]>([]);
  const [apiCollaborators, setApiCollaborators] = useState<CollaborationUser[]>([]);
  const [versionAssignments, setVersionAssignments] = useState<Set<string>>(new Set()); // Track which users are assigned to this version
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && documentId) {
      loadCollaborationData();
    }
  }, [isOpen, documentId]);

  const cancelInvitation = async (invitationId: string, email: string) => {
    try {
      console.log('🗑️ [CollaborationSettings] Cancelling invitation:', { invitationId, email });
      
      // Show confirmation dialog
      const confirmed = window.confirm(`Are you sure you want to cancel the invitation for ${email}?`);
      if (!confirmed) {
        return;
      }
      
      // Call API to cancel invitation
      const response = await fetch(`/api/v1/documents/${documentId}/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(keycloak.token),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to cancel invitation: ${response.statusText}`);
      }
      
      console.log('✅ [CollaborationSettings] Invitation cancelled successfully');
      
      // Refresh the invitations list
      await loadCollaborationData(true);
      
      // Show success message
      alert(`Invitation for ${email} has been cancelled.`);
      
    } catch (error) {
      console.error('❌ [CollaborationSettings] Failed to cancel invitation:', error);
      alert(`Failed to cancel invitation for ${email}. ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  const loadCollaborationData = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    console.log('🔍 [CollaborationSettings] Loading data for document:', documentId, forceRefresh ? '(FORCE REFRESH)' : '');
    
    try {
      // Load invitations and collaboration state in parallel
      const promises = [
        fetchInvitations(documentId, keycloak.token).catch(err => {
          console.error('❌ [CollaborationSettings] Failed to fetch invitations:', err);
          return { success: false, data: [], error: err.message };
        }),
        fetchCollaborationState(documentId, keycloak.token).catch(err => {
          console.error('❌ [CollaborationSettings] Failed to fetch collaboration state:', err);
          return { success: false, data: { recent_activity: [] }, error: err.message };
        })
      ];

      // If we're in version mode, also fetch version-specific assignments
      if (versionId) {
        promises.push(
          fetchVersionAssignments(documentId, versionId, keycloak.token).catch(err => {
            console.error('❌ [CollaborationSettings] Failed to fetch version assignments:', err);
            return { success: false, data: [], error: err.message };
          })
        );
      }

      const responses = await Promise.all(promises);
      const [invitationsResponse, collaborationResponse, versionAssignmentsResponse] = responses;

      console.log('📧 [CollaborationSettings] Invitations response:', invitationsResponse);
      console.log('👥 [CollaborationSettings] Collaboration response:', collaborationResponse);
      if (versionAssignmentsResponse) {
        console.log('🔗 [CollaborationSettings] Version assignments response:', versionAssignmentsResponse);
      }

      // Process version assignments data (if in version mode)
      if (versionId && versionAssignmentsResponse?.success && versionAssignmentsResponse.data) {
        const assignedUserIds = new Set<string>();
        
        // Handle different possible response structures
        const assignmentsData = versionAssignmentsResponse.data.data || versionAssignmentsResponse.data;
        const assignments = Array.isArray(assignmentsData) ? assignmentsData : assignmentsData.collaborators || [];
        
        assignments.forEach((assignment: any) => {
          // Add user ID to assignments set
          assignedUserIds.add(assignment.user_id || assignment.id || assignment.userId);
        });
        
        console.log('🔗 [CollaborationSettings] Version assignments processed:', Array.from(assignedUserIds));
        setVersionAssignments(assignedUserIds);
      }

      // Process invitations data
      if (invitationsResponse.success && invitationsResponse.data) {
        // Handle nested data structure from API: data.data.invitations
        const invitationsData = invitationsResponse.data.data || invitationsResponse.data;
        const invitations = Array.isArray(invitationsData) 
          ? invitationsData 
          : invitationsData.invitations || [];
        
        console.log('📧 [CollaborationSettings] Processed invitations array:', invitations);
        console.log('📧 [CollaborationSettings] Invitations array length:', invitations.length);
        
        const formattedInvitations: InvitationHistoryItem[] = invitations.map((inv: any) => ({
          id: inv.id,
          email: inv.invitee_name || inv.invitee_email || inv.email,
          role: inv.role,
          invitedAt: new Date(inv.created_at || inv.invited_at || inv.invitedAt),
          status: inv.status,
          invitedBy: inv.inviter?.name || inv.invited_by_name || inv.invitedBy || 'Unknown',
          expiresAt: inv.expires_at ? new Date(inv.expires_at) : (inv.invitation_expires_at ? new Date(inv.invitation_expires_at) : undefined),
        }));
        
        setInvitationHistory(formattedInvitations);
      }

      // Process activity data
      if (collaborationResponse.success && collaborationResponse.data?.data?.recent_activity) {
        const activities: ActivityLogItem[] = collaborationResponse.data.data.recent_activity.map((activity: any) => ({
          id: activity.id,
          type: activity.activity_type || activity.type,
          user: activity.user?.name || 
                (activity.user?.first_name && activity.user?.last_name 
                  ? `${activity.user.first_name} ${activity.user.last_name}`
                  : activity.user?.email || activity.user || 'Unknown User'),
          description: activity.description || getActivityDescription(activity.activity_type, activity.activity_data),
          timestamp: new Date(activity.created_at || activity.timestamp),
          metadata: activity.activity_data,
        }));
        
        setActivityLog(activities);
      }

      // Process collaborators data
      if (collaborationResponse.success && collaborationResponse.data?.data?.collaborators) {
        const collaboratorsData: CollaborationUser[] = collaborationResponse.data.data.collaborators
          .filter((collab: any) => collab.status !== 'removed') // Filter out removed collaborators
          .map((collab: any) => ({
            id: collab.id,
            name: collab.name || collab.email || 'Unknown User', // Use name from backend, fallback to email
            email: collab.email,
            role: collab.role,
            status: collab.status,
            lastActive: collab.last_active ? new Date(collab.last_active) : new Date(),
            joinedAt: collab.joined_at ? new Date(collab.joined_at) : new Date(),
            permissions: collab.permissions || {},
          }));
        
        console.log('👥 [CollaborationSettings] API Response collaborators:', collaborationResponse.data.data.collaborators);
        console.log('👥 [CollaborationSettings] Filtered collaborators (removed excluded):', collaboratorsData);
        setApiCollaborators(collaboratorsData);
      }
    } catch (err) {
      console.error('Failed to load collaboration data:', err);
      setError('Failed to load collaboration data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getActivityDescription = (activityType: string, activityData?: any): string => {
    switch (activityType) {
      case 'user_joined':
        return 'joined the collaboration';
      case 'user_left':
        return 'left the collaboration';
      case 'role_changed':
        return `role changed to ${activityData?.new_role || 'unknown'}`;
      case 'comment_added':
        return `added a comment${activityData?.location ? ` on ${activityData.location}` : ''}`;
      case 'change_tracked':
        return `made changes${activityData?.section ? ` to ${activityData.section}` : ''}`;
      case 'invitation_sent':
        return `invited ${activityData?.email || 'a user'} to collaborate`;
      case 'invitation_accepted':
        return 'accepted collaboration invitation';
      case 'document_shared':
        return 'shared the document';
      default:
        return activityData?.description || 'performed an action';
    }
  };

  // Version assignment handlers
  const handleAssignToVersion = async (userId: string) => {
    if (!versionId) return;
    
    try {
      console.log('🔗 [CollaborationSettings] Assigning user to version:', { userId, versionId });
      
      await assignUserToVersion(documentId, versionId, userId, keycloak.token);
      
      // Update local state immediately for better UX
      setVersionAssignments(prev => new Set([...prev, userId]));
      
      console.log('✅ [CollaborationSettings] User assigned to version successfully');
      
      // Optionally refresh data to get updated state
      if (onAssignToVersion) {
        onAssignToVersion(userId);
      }
      
    } catch (error) {
      console.error('❌ [CollaborationSettings] Failed to assign user to version:', error);
      alert(`Failed to assign user to version. ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  const handleUnassignFromVersion = async (userId: string) => {
    if (!versionId) return;
    
    try {
      console.log('🔗 [CollaborationSettings] Unassigning user from version:', { userId, versionId });
      
      await unassignUserFromVersion(documentId, versionId, userId, keycloak.token);
      
      // Update local state immediately for better UX
      setVersionAssignments(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
      
      console.log('✅ [CollaborationSettings] User unassigned from version successfully');
      
      // Optionally refresh data to get updated state
      if (onUnassignFromVersion) {
        onUnassignFromVersion(userId);
      }
      
    } catch (error) {
      console.error('❌ [CollaborationSettings] Failed to unassign user from version:', error);
      alert(`Failed to unassign user from version. ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  if (!isOpen) return null;

  // Use API collaborators if available, otherwise fall back to props
  const displayCollaborators = apiCollaborators.length > 0 ? apiCollaborators : collaborators;

  const tabs = [
    { id: 'collaborators' as const, label: 'Collaborators', count: displayCollaborators.length },
    { id: 'invitations' as const, label: 'Invitations', count: invitationHistory.length },
    { id: 'activity' as const, label: 'Activity', count: activityLog.length },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-[500px] bg-white dark:bg-gray-900 shadow-xl transform transition-transform">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {versionId ? `${versionNumber || 'Version'} - ${documentTitle || 'Document'}` : 'Collaboration Settings'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {versionId ? 'Assign collaborators to this version' : 'Manage collaborators and permissions'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => loadCollaborationData(true)}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md transition-colors disabled:opacity-50"
              title="Force refresh collaboration data"
            >
              <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-md transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex-1 px-4 py-3 text-sm font-medium transition-colors
                ${activeTab === tab.id
                  ? 'bg-lex-accent-soft text-lex-accent border-b-2 border-lex-accent'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }
              `}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="ml-2 bg-gray-200 dark:bg-gray-700 text-xs px-2 py-0.5 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              <button
                onClick={() => loadCollaborationData()}
                className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 underline"
              >
                Try again
              </button>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400">Loading collaboration data...</span>
            </div>
          )}

          {!loading && !error && (
            <>
              {activeTab === 'collaborators' && (
                <div className="space-y-4">
                  {/* Add collaborator button */}
                  {isOwner && (
                    <button
                      onClick={onInviteUser}
                      className="w-full flex items-center justify-center space-x-2 p-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-lex-accent transition-colors"
                    >
                      <UserPlusIcon className="w-5 h-5 text-gray-400" />
                      <span className="text-gray-600 dark:text-gray-400">Invite New Collaborator</span>
                    </button>
                  )}

                  {/* Collaborators list */}
                  <div className="space-y-3">
                    {displayCollaborators.length > 0 ? (
                      displayCollaborators.map((collaborator) => (
                        <CollaboratorCard
                          key={collaborator.id}
                          collaborator={collaborator}
                          isOwner={collaborator.role === 'owner'}
                          isCurrentUser={collaborator.id === currentUser?.id}
                          canManage={isOwner}
                          onUpdateRole={onUpdateRole}
                          onRemoveUser={onRemoveUser}
                          isVersionMode={!!versionId}
                          isAssignedToVersion={versionAssignments.has(collaborator.id)}
                          onAssignToVersion={handleAssignToVersion}
                          onUnassignFromVersion={handleUnassignFromVersion}
                        />
                      ))
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <UserGroupIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No collaborators yet</p>
                        <p className="text-sm">Invite users to start collaborating</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'invitations' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                      Invitation History
                    </h3>
                    {isOwner && (
                      <button
                        onClick={onInviteUser}
                        className="text-sm text-lex-accent hover:text-lex-accent-hover"
                      >
                        Send New Invitation
                      </button>
                    )}
                  </div>
                  {invitationHistory.length > 0 ? (
                    <InvitationHistorySection 
                      invitations={invitationHistory} 
                      onCancelInvitation={cancelInvitation}
                    />
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <BellIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No invitations sent yet</p>
                      <p className="text-sm">Invitation history will appear here</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'activity' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                    Recent Activity
                  </h3>
                  {activityLog.length > 0 ? (
                    <ActivityLogSection activities={activityLog} />
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <PencilIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No recent activity</p>
                      <p className="text-sm">Collaboration activity will appear here</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollaborationSettingsDrawer; 